import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Stack, Paper, TextField, Button, Chip,
  IconButton, Tooltip, Alert, Select, MenuItem,
  FormControl, InputLabel, Grid, CircularProgress,
} from "@mui/material";
import { WhatsApp, OpenInNew, Refresh, ContentCopy } from "@mui/icons-material";
import { usersAdminAPI, AdminUser } from "../../services/adminApi";
import { CopyButton } from "./marketingShared";

type Segment = "all" | "basic" | "premium" | "pro" | "no_subscription";

const SEGMENTS: { value: Segment; label: string }[] = [
  { value: "all",              label: "All Users" },
  { value: "no_subscription",  label: "No Active Subscription (Leads)" },
  { value: "basic",            label: "Plan: Basic" },
  { value: "premium",          label: "Plan: Premium" },
  { value: "pro",              label: "Plan: Pro" },
];

const DEFAULT_TEMPLATE = "Hi {name}! 👋 Just checking in — your {plan} access on Dr.Paw is ready. Let us know if you have any questions!";

function digitsOnly(phone: string) {
  return phone.replace(/[^\d]/g, "");
}

function fillTemplate(template: string, user: AdminUser) {
  return template
    .replace(/\{name\}/g, user.name || "there")
    .replace(/\{plan\}/g, user.subscription?.plan || "free");
}

const AdminMarketingWhatsApp: React.FC = () => {
  const [users, setUsers]       = useState<AdminUser[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [segment, setSegment]   = useState<Segment>("all");
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);

  useEffect(() => {
    usersAdminAPI.list({ limit: 500 })
      .then(res => setUsers(res.data?.data || []))
      .catch(() => setError("Could not load users from the backend."))
      .finally(() => setLoading(false));
  }, []);

  const filteredUsers = useMemo(() => {
    const withPhone = users.filter(u => !!u.phone?.trim());
    switch (segment) {
      case "no_subscription":
        return withPhone.filter(u => !u.subscription || u.subscription.status !== "active");
      case "basic":
      case "premium":
      case "pro":
        return withPhone.filter(u => u.subscription?.status === "active" && u.subscription.plan === segment);
      default:
        return withPhone;
    }
  }, [users, segment]);

  const usersWithoutPhone = users.length - users.filter(u => !!u.phone?.trim()).length;

  const previewUser = filteredUsers[0];
  const previewText = previewUser ? fillTemplate(template, previewUser) : template;

  const buildLink = (user: AdminUser) =>
    `https://wa.me/${digitsOnly(user.phone || "")}?text=${encodeURIComponent(fillTemplate(template, user))}`;

  const handleCopyAll = () => {
    const allLinks = filteredUsers.map(u => `${u.name}: ${buildLink(u)}`).join("\n");
    navigator.clipboard.writeText(allLinks);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      {/* ── Page Hero ── */}
      <Box sx={{
        mb: 4, p: { xs: 2.5, md: 3.5 }, borderRadius: "20px",
        background: "linear-gradient(135deg,#0D47A1 0%,#1565C0 55%,#1976D2 100%)",
        boxShadow: "0 8px 32px rgba(21,101,192,.3)",
      }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <WhatsApp sx={{ color: "#fff", fontSize: "1.6rem" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>
                WhatsApp Broadcast
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,.7)", mt: 0.3 }}>
                Generate personalized wa.me links for a segment of users to send manually
              </Typography>
            </Box>
          </Stack>
          <Chip label="Manual send — no API" size="small" sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", fontWeight: 600, fontSize: ".72rem" }} />
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
            <Box sx={{ px: 2.5, py: 2, bgcolor: "#1565C0" }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Audience</Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.6)" }}>Choose who receives this message</Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Segment</InputLabel>
                <Select label="Segment" value={segment} onChange={(e) => setSegment(e.target.value as Segment)}>
                  {SEGMENTS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
              {loading ? (
                <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`${filteredUsers.length} recipients`} sx={{ fontWeight: 700, bgcolor: "rgba(21,101,192,.1)", color: "#1565C0" }} />
                  {usersWithoutPhone > 0 && (
                    <Chip label={`${usersWithoutPhone} excluded (no phone)`} sx={{ fontWeight: 600, bgcolor: "rgba(239,68,68,.08)", color: "#EF4444" }} />
                  )}
                </Stack>
              )}
            </Box>
          </Paper>

          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
            <Box sx={{ px: 2.5, py: 2, bgcolor: "#0A1628" }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Message Template</Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>Use {"{name}"} and {"{plan}"} as placeholders</Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              <TextField
                multiline rows={5} fullWidth
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              />
              <Box sx={{ mt: 2, p: 1.5, borderRadius: "10px", bgcolor: "#F0F7FF", border: "1px solid #DDEEFF" }}>
                <Typography variant="caption" sx={{ color: "#78909C", display: "block", mb: 0.5 }}>Preview ({previewUser ? previewUser.name : "no recipients"}):</Typography>
                <Typography variant="body2" sx={{ color: "#0A1628", whiteSpace: "pre-line" }}>{previewText}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
            <Box sx={{ px: 2.5, py: 2, bgcolor: "#F0F7FF", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>Recipients ({filteredUsers.length})</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={handleCopyAll} disabled={filteredUsers.length === 0} sx={{ textTransform: "none", color: "#1565C0", fontSize: ".8rem" }}>Copy All Links</Button>
                <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => setSegment("all")} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Reset</Button>
              </Stack>
            </Box>
            <Box sx={{ p: 2, maxHeight: 600, overflowY: "auto" }}>
              {filteredUsers.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center", color: "#78909C" }}>
                  <WhatsApp sx={{ fontSize: 48, color: "#C8E6C9", mb: 1 }} />
                  <Typography variant="body2">No recipients match this segment.</Typography>
                </Box>
              ) : filteredUsers.map((u) => {
                const link = buildLink(u);
                return (
                  <Box key={u._id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, mb: 1, borderRadius: "10px", border: "1px solid #E8EDF5", "&:last-child": { mb: 0 } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: ".88rem", color: "#0A1628" }}>{u.name}</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C" }}>{u.phone} · {u.subscription?.plan || "no plan"}</Typography>
                    </Box>
                    <CopyButton text={link} small />
                    <Tooltip title="Open in WhatsApp">
                      <IconButton size="small" onClick={() => window.open(link, "_blank")} sx={{ color: "#22C55E", borderRadius: "8px" }}>
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminMarketingWhatsApp;
